import React from 'react';
import PropTypes from 'prop-types';
import Box from '@tds/core-box';
import Text from '@tds/core-text';
import { or, componentWithName } from '@tds/util-prop-types';
import styled from 'styled-components';
import { safeRest } from '@tds/util-helpers';
import { borders, forms } from '@tds/shared-styles';
import { medium, boldFont } from '@tds/shared-typography';
import { media } from '@tds/core-responsive';

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var _extends_1 = createCommonjsModule(function (module) {
function _extends() {
  module.exports = _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

module.exports = _extends;
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

// colours
var colorTelusPurple = '#4b286d';
var colorWhite = '#fff'; // grey

function find(t,n,r){if("function"==typeof Array.prototype.find)return t.find(n,r);r=r||this;var f=t.length;if("function"!=typeof n)throw new TypeError(n+" is not a function");for(var o=0;o<f;o++)if(n.call(r,t[o],o,t))return t[o]}

var sanitize = function sanitize(text) {
  return text.toString().toLowerCase().replace(/ /g, '-').replace(/[^a-zA-Z0-9-]/g, '');
};

var generateId = function generateId() {
  for (var _len = arguments.length, choices = new Array(_len), _key = 0; _key < _len; _key++) {
    choices[_key] = arguments[_key];
  }

  var id = sanitize(find(choices, function (choice) {
    return choice;
  }));
  return {
    identity: function identity() {
      return id;
    },
    postfix: function postfix(value) {
      return "".concat(id, "_").concat(sanitize(value));
    }
  };
};

var baseButton = _extends_1({
  margin: 0,
  padding: '0 2rem',
  cursor: 'pointer',
  background: 'none',
  transition: 'background 0.2s',
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center'
}, media.from('md').css({
  display: 'inline-flex',
  width: 'auto',
  minWidth: '136px'
}));

var StyledButtonGroupItem = styled.div.withConfig({
  displayName: "ButtonGroupItem__StyledButtonGroupItem",
  componentId: "drabnz-0"
})({
  margin: '0.5rem 0'
});
var StyledInput = styled.input.withConfig({
  displayName: "ButtonGroupItem__StyledInput",
  componentId: "drabnz-1"
})({
  position: 'fixed',
  opacity: '0',
  '&:checked ~ label': {
    backgroundColor: colorTelusPurple,
    boxShadow: "0px 0px 0px 0px ".concat(colorTelusPurple),
    color: colorWhite
  },
  '&:focus ~ label': {
    boxShadow: "0px 0px 0px 2px ".concat(colorTelusPurple, ", 0px 0px 8px 1px ").concat(colorTelusPurple)
  }
});
var StyledLabel = styled.label.withConfig({
  displayName: "ButtonGroupItem__StyledLabel",
  componentId: "drabnz-2"
})(borders.none, borders.rounded, medium, boldFont, forms.buttonHeight, forms.font, baseButton, {
  transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
  backgroundColor: colorWhite,
  boxShadow: "0px 0px 0px 1px ".concat(colorTelusPurple),
  color: colorTelusPurple,
  whiteSpace: 'nowrap',
  minWidth: '136px',
  '&:hover': {
    backgroundColor: colorWhite,
    color: colorTelusPurple,
    boxShadow: "0px 0px 0px 2px ".concat(colorTelusPurple, ", 0px 0px 8px 1px ").concat(colorTelusPurple)
  }
});
var ButtonGroupItem = React.forwardRef(function (_ref, ref) {
  var name = _ref.name,
      value = _ref.value,
      checked = _ref.checked,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      children = _ref.children,
      defaultChecked = _ref.defaultChecked,
      readOnly = _ref.readOnly,
      rest = objectWithoutProperties(_ref, ["name", "value", "checked", "onChange", "onFocus", "onBlur", "children", "defaultChecked", "readOnly"]);

  var itemId = generateId(name).postfix(value);
  return React.createElement(StyledButtonGroupItem, safeRest(rest), React.createElement(StyledInput, {
    id: itemId,
    name: name,
    value: value,
    type: "radio",
    checked: checked,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    defaultChecked: defaultChecked,
    readOnly: readOnly,
    ref: ref
  }), React.createElement(StyledLabel, {
    htmlFor: itemId
  }, children));
});
ButtonGroupItem.propTypes = {
  /**
   *@ignore
   *
   * The name of the ButtonGroup this item is in. (Passed in from parent)
   */
  name: PropTypes.string,

  /**
   * The value of this button.
   */
  value: PropTypes.string.isRequired,

  /**
   *@ignore
   *
   * The current selected value of the full ButtonGroup. (Passed in from parent)
   */
  checked: PropTypes.bool,

  /**
   *@ignore
   *
   * A callback to communicate which button has been selected. (Passed in from parent)
   */
  onChange: PropTypes.func,

  /**
   *@ignore
   *
   * A callback that triggers when a button is given focus. (Passed in from parent)
   */
  onFocus: PropTypes.func,

  /**
   *@ignore
   *
   * A callback that triggers when a button loses focus. (Passed in from parent)
   */
  onBlur: PropTypes.func,

  /**
   *@ignore
   *
   * React defaultChecked
   * https://reactjs.org/docs/uncontrolled-components.html#default-values
   */
  defaultChecked: PropTypes.bool,

  /**
   *@ignore
   *
   * HTML readOnly
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#readonly
   */
  readOnly: PropTypes.bool,

  /**
   * The button's label. It can include the `A11yContent` component or strings.
   */
  children: or([PropTypes.string, componentWithName('A11yContent')]).isRequired
};
ButtonGroupItem.defaultProps = {
  checked: undefined,
  name: undefined,
  onChange: undefined,
  onFocus: undefined,
  onBlur: undefined,
  defaultChecked: undefined,
  readOnly: undefined
};
ButtonGroupItem.displayName = 'ButtonGroup.Item';

var StyledButtonGroup = styled(Box)({
  flexFlow: 'row wrap',
  maxWidth: '784px'
});
/**
 * An input component utilizing buttons that act as radios.
 * @version ./package.json
 */

var ButtonGroup = React.forwardRef(function (_ref, ref) {
  var name = _ref.name,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      value = _ref.value,
      label = _ref.label,
      children = _ref.children,
      readOnly = _ref.readOnly,
      rest = objectWithoutProperties(_ref, ["name", "onChange", "onFocus", "onBlur", "value", "label", "children", "readOnly"]);

  var passedButtons = React.Children.map(children, function (child) {
    return React.cloneElement(child, {
      name: name,
      onChange: onChange,
      onFocus: onFocus,
      onBlur: onBlur,
      checked: typeof value !== 'undefined' ? value === child.props.value : undefined,
      readOnly: readOnly
    });
  });
  var buttonValues = [];
  Object.keys(passedButtons).forEach(function (key) {
    buttonValues.push(passedButtons[key].props.value);
  });
  return React.createElement("fieldset", _extends_1({}, safeRest(rest), {
    name: name,
    ref: ref
  }), React.createElement("legend", null, React.createElement(Text, {
    bold: true,
    size: "medium"
  }, label)), React.createElement(StyledButtonGroup, {
    between: 3,
    inline: true
  }, passedButtons));
});
ButtonGroup.displayName = 'ButtonGroup';
ButtonGroup.propTypes = {
  /**
   * The form name of the ButtonGroup.
   */
  name: PropTypes.string.isRequired,

  /**
   * The current selected value for the group.
   */
  value: PropTypes.string,

  /**
   * A label to be displayed above the ButtonGroup.
   */
  label: PropTypes.string.isRequired,

  /**
   * A callback function to handle changing which button is seleced. Passed into all buttons.
   *
   * @param {SyntheticEvent} event The React `SyntheticEvent`
   */
  onChange: PropTypes.func,

  /**
   * A callback function to be invoked when a button receives focus. Passed into all buttons.
   *
   * @param {SyntheticEvent} event The React `SyntheticEvent`
   */
  onFocus: PropTypes.func,

  /**
   * A callback function to be invoked when a button loses focus. Passed into all buttons.
   *
   * @param {SyntheticEvent} event The React `SyntheticEvent`
   */
  onBlur: PropTypes.func,

  /**
   * @ignore
   *
   * A callback function to be invoked when a button loses focus. Passed into all buttons.
   */
  readOnly: PropTypes.bool,

  /**
   * A group of ButtonGroup.Item components.
   */
  children: componentWithName('ButtonGroup.Item', true).isRequired
};
ButtonGroup.defaultProps = {
  onFocus: undefined,
  onBlur: undefined,
  onChange: undefined,
  value: undefined,
  readOnly: undefined
};
ButtonGroup.Item = ButtonGroupItem;

export default ButtonGroup;
